import { Deferred } from '@inertiajs/react'
import WorkerStatsGrid from '@/frontend/components/DigitalWorkers/WorkerStatsGrid'
import PendingApprovalsCard from '@/frontend/components/approvals/PendingApprovalsCard'
import { ProcessesPanel } from '@/frontend/features/HomeWork/ProcessesPanel'
import { WorkerActivityPanel, WorkerActivityPanelSkeleton } from './WorkerActivityPanel'
import { WorkerAgendaPanel, WorkerAgendaPanelSkeleton } from './WorkerAgendaPanel'
import type { ApprovalItem } from '@/frontend/components/approvals/ApprovalQueue'
import type { WorkerFeedPayload } from '@/frontend/features/Home/types'
import type { WorkProcess } from '@/frontend/features/HomeWork/types'
import type { CronData, DigitalWorkerDetail, WorkerStats } from '@/frontend/pages/Dashboard/DigitalWorkers/types'

interface Props {
  worker: DigitalWorkerDetail
  stats?: WorkerStats
  // Deferidos na landing (grupo "fleet"): alimentam o painel Agendado e a
  // contagem real do stat de Rotinas. Ausentes com a instância não-pronta.
  crons?: CronData | null
  // Deferido (prop worker_feed da Show) — o skeleton cobre o vão.
  workerFeed?: WorkerFeedPayload
  processes: WorkProcess[]
  approvalQueueEnabled: boolean
  pendingApprovals: ApprovalItem[]
}

/**
 * Dashboard de operação do agente (Iteração 3, Onda B — protótipo v2
 * aprovado): a Visão geral deixa de ser a mini-home chat-first e vira o
 * painel de OPERAÇÃO do agente, dentro da moldura intacta do
 * WorkerPortalShell. Ordem:
 *
 * 1. Strip dos 3 stats (Disponível hoje · Conversas ativas 15 min · Rotinas) —
 *    cards-link mantidos; a contagem de Rotinas hidrata quando os crons
 *    deferidos chegam.
 * 2. Duas colunas: "O que a {Nome} fez" (worker_feed reenquadrado — a antiga
 *    seção "Enquanto você esteve fora") | "Agendado" (crons com next_run).
 * 3. "Processos vinculados" em LISTA (mesmo ProcessesPanel da home — linhas,
 *    não cards soltos). É onde a Visão geral termina.
 *
 * "Começar uma conversa" saiu (Iteração 3, Ajuste D): com o chat virando aba
 * própria do portal (Ajustes B/C), o composer compacto no pé da Visão geral
 * ficou redundante — a entrada de conversa mora na aba Chat. A Visão geral é
 * operação pura.
 *
 * Assinatura NÃO mora mais aqui (Iteração 3, Ajuste 3): o card de billing saiu
 * da Visão geral e passou a viver só em Perfil › Configurações — a Visão geral
 * é operação, não conta.
 *
 * Honestidade (INVESTIGACAO-DADOS): só dado real — nada de execução
 * fabricada; processos mostram nome/status, sem "última execução".
 */
export default function WorkerOperationsDashboard({
  worker, stats, crons = null, workerFeed, processes,
  approvalQueueEnabled, pendingApprovals,
}: Props) {
  const isReady = worker.provisioning_status === 'ready'

  return (
    <div className="animate-fade-up space-y-7" data-testid="worker-operations-dashboard">
      {stats && <WorkerStatsGrid workerId={worker.id} stats={stats} crons={crons} />}

      {approvalQueueEnabled && <PendingApprovalsCard items={pendingApprovals} />}

      <div className="grid items-start gap-x-5 gap-y-7 md:grid-cols-[1.4fr_1fr]">
        <Deferred data="worker_feed" fallback={<WorkerActivityPanelSkeleton workerName={worker.name} />}>
          <WorkerActivityPanel workerName={worker.name} feed={workerFeed} />
        </Deferred>
        {isReady ? (
          <Deferred data="crons" fallback={<WorkerAgendaPanelSkeleton />}>
            <WorkerAgendaPanel crons={crons} />
          </Deferred>
        ) : (
          // Sem instância pronta os crons nem chegam (prop optional): o
          // painel explica em vez de pendurar um skeleton eterno.
          <WorkerAgendaPanel crons={null} instanceNotReady />
        )}
      </div>

      <ProcessesPanel processes={processes} title="Processos vinculados" showWorker={false} />
    </div>
  )
}
